import { cn } from '@my/ui';
import { Plus, Trash2 } from 'lucide-react';
import {
  SECTION_PRESETS,
  newSection,
  type CheckpointTemplate,
  type Section,
} from '../lib/model';
import type { Catalogue } from '../lib/conditions';
import { AppliesWhenCard } from './AppliesWhenCard';
import { CheckpointSection } from './CheckpointSection';

export function TemplateEditor({
  template,
  onChange,
  onDelete,
  catalogue,
}: {
  template: CheckpointTemplate;
  onChange: (next: CheckpointTemplate) => void;
  onDelete: () => void;
  catalogue: Catalogue;
}) {
  const set = (patch: Partial<CheckpointTemplate>) => onChange({ ...template, ...patch });
  const setSection = (next: Section) =>
    set({ sections: template.sections.map((s) => (s.id === next.id ? next : s)) });
  const total = template.sections.reduce((n, s) => n + s.checkpoints.length, 0);

  const addSection = (key: string) => {
    const preset = SECTION_PRESETS.find((p) => p.key === key);
    if (!preset) return;
    set({
      sections: [...template.sections, newSection('task', { name: preset.name, taskTypes: [...preset.taskTypes] })],
    });
  };

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <input
              value={template.name}
              onChange={(e) => set({ name: e.target.value })}
              placeholder="Template name"
              aria-label="Template name"
              className="w-full rounded border border-transparent px-1 text-base font-semibold text-slate-900 placeholder:text-slate-300 hover:border-slate-200 focus:border-sky-300 focus:outline-none"
            />
            <input
              value={template.description}
              onChange={(e) => set({ description: e.target.value })}
              placeholder="What it is for"
              aria-label="Description"
              className="mt-0.5 w-full rounded border border-transparent px-1 text-xs text-slate-500 placeholder:text-slate-300 hover:border-slate-200 focus:border-sky-300 focus:outline-none"
            />
          </div>
          <span className="mt-1 shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">
            {total} checkpoint{total === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            onClick={() => set({ active: !template.active })}
            className="mt-1 flex shrink-0 items-center gap-1.5 text-xs text-slate-600"
            aria-pressed={template.active}
          >
            <span
              className={cn(
                'flex h-4 w-7 items-center rounded-full p-0.5 transition-colors',
                template.active ? 'bg-emerald-500' : 'bg-slate-300',
              )}
            >
              <span
                className={cn('h-3 w-3 rounded-full bg-white transition-transform', template.active && 'translate-x-3')}
              />
            </span>
            {template.active ? 'Active' : 'Off'}
          </button>
          <button
            type="button"
            onClick={onDelete}
            aria-label="Delete template"
            title="Delete template"
            className="mt-1 shrink-0 text-slate-300 hover:text-red-600"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <AppliesWhenCard
        value={template.appliesWhen}
        onChange={(appliesWhen) => set({ appliesWhen })}
        catalogue={catalogue}
      />

      {template.sections.map((s) => (
        <CheckpointSection
          key={s.id}
          section={s}
          onChange={setSection}
          onRemove={
            s.scope === 'task'
              ? () => set({ sections: template.sections.filter((x) => x.id !== s.id) })
              : undefined
          }
        />
      ))}

      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-slate-300 px-4 py-2">
        <span className="text-[11px] text-slate-500">Add checks at a kind of stop:</span>
        {SECTION_PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => addSection(p.key)}
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] text-sky-700 ring-1 ring-sky-200 hover:bg-sky-50"
          >
            <Plus className="h-3 w-3" /> {p.name}
          </button>
        ))}
      </div>
    </div>
  );
}
